<script>
import { mapGetters } from 'vuex'
import Modal from '@/components/atoms/Modal'
import Diets from '@/components/atoms/Diets'
import ImageWrapper from '@/components/atoms/ImageWrapper'
import ButtonLoader from '@/components/atoms/ButtonLoader'
import InputStep from '@/components/molecules/input/InputStep'
import DisplayRate from '@/components/organism/restaurant/DisplayRate'
import MealDetails from '@/components/organism/restaurant/MealDetails'
import MealOptionGroup from '@/components/organism/restaurant/MealOptionGroup'
import isEmpty from 'lodash.isempty'
import { EventBus } from '@/modules/plugins/modules/event'

export default {
    name: 'SelectMeal',
    components: {
        MealDetails,
        DisplayRate,
        ImageWrapper,
        Diets,
        ButtonLoader,
        MealOptionGroup,
        InputStep,
        Modal,
    },
    props: {
        details: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            loadingBack: false,
            loadingAddMeal: false,
            loadingNewCart: false,
            meal: null,
            ready: null,
            showHelp: false,
            tooltip: null,
            timeout: null,
            temporaryRestaurant: null,
            isOpen: false,
            selectedMeal: null,
            maxQuantity: 999,
        }
    },
    computed: {
        ...mapGetters({
            currency: 'active/currency',
            orderRestaurant: 'order/restaurant',
            orderAddress: 'order/address',
            activeRestaurant: 'active/restaurant',
            address: 'order/address',
            time: 'order/time',
            lgDevice: 'lgDevice',
            smDevice: 'smDevice',
        }),
        cart() {
            return this.$cart
        },
        restaurant() {
            return this.$restaurant
        },
        optionGroups() {
            return this.details?.option_groups ? this.sortNull(this.details.option_groups) : []
        },
        scrollContainer() {
            return `scrollContainer-${this._uid}`
        },
        price() {
            return this.meal
                ? this.meal.children.reduce(
                      (p, c) => p + c.price * (c.quantity || 1) * this.meal.quantity,
                      this.details.price * this.meal.quantity
                  )
                : undefined
        },
        originalPrice() {
            return this.meal
                ? this.meal.children.reduce((p, c) => {
                      const cPrice = c.original_price || c.price
                      return p + cPrice * (c.quantity || 1) * this.meal.quantity
                  }, (this.details.original_price || this.details.price) * this.meal.quantity)
                : undefined
        },
        notReady() {
            return this.ready ? this.ready.includes(false) : false
        },
        helpText() {
            if (!this.showHelp || (this.showHelp && !this.notReady)) return
            let text = `${this.$t('meal.please_choose')} `
            let first = true
            this.details?.option_groups?.forEach((group, index) => {
                if (!this.ready[index]) {
                    if (!first) text = text.concat(', ')
                    text = text.concat(group.name)

                    first = false
                }
            })
            return text
        },
        isActive() {
            return this.details.is_active && !this.inactiveReason
        },
        inactiveReason() {
            return this.cart.inactiveItems.find((item) =>
                item ? item.id == this.details?.id : false
            )?.message
        },
        minQuantity() {
            return this.details.minimum_quantity || 1
        },
        disabled() {
            return (
                !this.isActive ||
                this.loadingAddMeal ||
                isNaN(this.meal.quantity) ||
                this.meal.quantity < this.minQuantity ||
                this.meal.quantity > this.maxQuantity
            )
        },
        isCheckout() {
            return this.$route.name === 'checkout'
        },
    },
    mounted() {
        EventBus.$on('selectMeal', this.selectMealEvent)
        EventBus.$on('openMeal', this.openMealEvent)
    },
    beforeDestroy() {
        EventBus.$off('selectMeal')
        EventBus.$off('openMeal')
    },
    methods: {
        async selectMealEvent(meal) {
            this.meal = meal
            await this.initMeal(meal.id, meal.recommender)
            if (this.notReady) {
                this.setQuery({ meal: meal.id })
                this.$refs.selectMeal.openModal()
            } else {
                this.selectMeal(false)
            }
        },
        openMealEvent(meal) {
            this.openMealSelector(meal.id, meal.recommender)
        },
        cancel() {
            this.showHelp = false
            this.$refs.cartExist.closeModal()
        },
        async initMeal(id, recommender) {
            this.resetMeal()
            await this.details.fetch(id)
            this.$set(this.meal, 'quantity', this.details.minimum_quantity || 1)
            this.$set(this.meal, 'meal', this.details.link)
            this.$set(this.meal, 'name', this.details.model.name)
            this.$set(this.meal, 'price', this.details.model.price)
            this.$set(this.meal, 'original_price', this.details.model.original_price)
            this.$set(this.meal, 'restaurant_id', this.restaurant?.id)
            if (recommender) this.$set(this.meal, 'source', recommender)
            if (this.details.option_groups && this.details.option_groups.length)
                this.ready = this.details.option_groups.map((group) => group.min_quantity === 0)
        },
        async openMealSelector(id, recommender) {
            try {
                await this.initMeal(id, recommender)
                this.setQuery({ meal: id })
                this.$refs.selectMeal.openModal()
                return Promise.resolve()
            } catch {
                return Promise.resolve()
            }
        },
        async open(id) {
            if (this.isOpen) return
            if (isEmpty(this.orderAddress) && !this.$cart.isCanteen) {
                if (this.$user.isLoggedIn) this.$location.select.open(() => this.open(id))
                else this.$location.enter.open(() => this.open(id), true)
                return
            }

            if (this.cart.hasTemporary && this.cart.isEmpty) {
                await this.newCart(id)
            } else {
                await this.openMealSelector(id)
            }
            return Promise.resolve()
        },
        setState(isOpen) {
            this.$emit('input', isOpen)
        },
        async confirmSelection() {
            await this.newCart(undefined, false)
            await this.selectMeal()
        },
        async newCart(id, openSelector = true) {
            this.timeout = setTimeout(() => {
                this.loadingNewCart = true
            }, 1000)
            this.cart.resetTemporary()
            await this.$store.dispatch('active/resetCanteen')
            await this.$store.dispatch('order/setAddress', this.$store.getters['order/address'])
            this.cart.resetCart()
            this.cart.restaurant = this.restaurant.link
            this.$refs.cartExist?.closeModal()

            if (openSelector) await this.openMealSelector(id)
            clearTimeout(this.timeout)
            this.timeout = null
            this.selectedMeal = null
            this.loadingNewCart = false
            return Promise.resolve()
        },
        selectMealOptionGroup(groupId, payload, index) {
            this.$set(
                this.meal,
                'children',
                this.meal.children?.filter((option) => option.mealoptiongroup_id !== groupId)
            )
            if (payload?.selection?.length) {
                this.$set(this.meal, 'children', this.meal.children.concat(payload.selection))
            }
            if (this.ready) this.$set(this.ready, index, payload.ready)
        },
        async selectMeal(close = true) {
            if (this.notReady) {
                this.showHelp = true
                this.$scrollTo(`#mealOptionGroup-${this.ready.findIndex((ready) => !ready)}`, {
                    container: `#${this.scrollContainer}`,
                    offset: 0,
                })
            } else if (this.cart.hasTemporary && !this.cart.isEmpty) {
                this.$refs.cartExist.openModal()
            } else {
                try {
                    if (this.cart.restaurant !== this.restaurant.link) {
                        // just in case
                        this.cart.restaurant = this.restaurant.link
                    }
                    this.cart.verifyMeals()
                    this.showHelp = false
                    this.loadingAddMeal = true
                    this.$store.dispatch('recommender/setLoader', this.details.id)
                    if (this.meal?.meal) {
                        const index = this.cart.meals.findIndex(
                            (meal) =>
                                meal.meal === this.meal.meal &&
                                this.isDeepEqual(this.meal.children, meal.children)
                        )
                        if (index >= 0) {
                            this.cart.addMeal(index, this.meal.quantity)
                        } else {
                            this.cart.pushMeal(this.meal)
                        }
                    }
                    this.$store.dispatch('order/setRestaurant', this.restaurant.model)
                    await this.cart.calculate()
                    this.loadingAddMeal = false
                    this.$store.dispatch('recommender/setLoader')
                    if (close) this.$refs.selectMeal.closeModal()
                    this.resetMeal()
                } catch (e) {
                    // eslint-disable-next-line prettier/prettier, no-console
                    console.error(e)
                    this.loadingAddMeal = false
                    this.$store.dispatch('recommender/setLoader')
                    this.cart.meals = this.cart?.meals?.filter((item) =>
                        this.cart?.calculation?.items?.find(
                            (calculatedItem) =>
                                item.meal === calculatedItem.meal &&
                                this.isDeepEqual(item.children, calculatedItem.children)
                        )
                    )
                }
            }
        },
        resetMeal() {
            this.setQuery({ meal: undefined })
            this.$set(this, 'meal', {
                quantity: 1,
                children: [],
                name: null,
                price: null,
                original_price: null,
                meal: null,
                restaurant_id: null,
            })
        },
        close() {
            this.$refs.selectMeal.closeModal()
        },
        reset() {
            this.details.reset()
            this.ready = null
            this.showHelp = false
            this.tooltip = null
            this.temporaryRestaurant = null
            this.resetMeal()
        },
    },
}
</script>

<template lang="pug">
.select-meal-wrapper
    modal(ref="cartExist" :closable="false")
        template(v-slot:header)
            .modal-header-text {{$t('meal.new_cart_title')}}
        template(v-slot:body)
            p.body-m(v-if="orderRestaurant && activeRestaurant") {{$t('meal.new_cart_desc', {oldRestaurant: orderRestaurant.name, newRestaurant: activeRestaurant.name})}}
        template(v-slot:footer)
            button.btn.btn-secondary.btn-block(@click="cancel" :disabled="loadingNewCart") {{$t('meal.view_menu')}}
            button-loader.btn.btn-primary.btn-block(@click="confirmSelection" :loading="loadingNewCart" inline) {{$t('meal.new_cart')}}

    modal.select-meal#select-meal(
        v-model="isOpen"
        ref="selectMeal" 
        :key="scrollContainer" 
        :scroll-id="scrollContainer" 
        @input="setState" 
        @close="reset" 
        @closed="reset"
        scroll hide-header slim-body)
        template(v-slot:body v-if="details")
            .position-sticky.right-fix-20.top-fix-20.z-1.h-0.float-right
                button.btn.btn-white-primary.btn-icon.rounded-circle.shadow-5(@click="close")
                    icon(icon="fa-solid fa-times" size="lg")
            image-wrapper.select-meal-cover(:src="details.image" fill)
            .px-5.pt-5
                meal-details(:details="details" :is-active="isActive" :inactive-reason="inactiveReason")
                meal-option-group.mt-7(
                    v-for="(optionGroup, index) in optionGroups"
                    :currency="currency"
                    :model="optionGroup"
                    :details="details"
                    :disabled="!isActive"
                    :hasError="showHelp && !ready[index]"
                    :key="`mealOptionGroup-${index}`"
                    :id="`mealOptionGroup-${index}`"
                    @select="selectMealOptionGroup(optionGroup.id, $event, index)")
        template(v-slot:footer v-if="details")
            span(v-if="minQuantity > 1" :class="{'position-absolute mt-11': smDevice}") {{$t("meal.minimum_quantity")}}&nbsp;{{minQuantity}}
            .d-inline-flex.align-items-center.ml-auto
                input-step.mb-0.mr-2.mr-sm-6(
                    v-if="meal"
                    v-model="meal.quantity"
                    :min="minQuantity"
                    :max="maxQuantity"
                    :class="{'mb-5 mb-lg-0': minQuantity > 1 && smDevice}"
                    popover-container="#select-meal"
                    :disabled="!isActive" variant xxs)
                button-loader.btn.btn-primary.btn-currency.flex-grow-0.px-3.px-sm-5.mr-16.mr-md-0.ml-auto(
                    @click="selectMeal"
                    :disabled="disabled"
                    v-tooltip.hover.top.html="helpText"     
                    :key="`addMeal-${helpText}`"           
                    :class="{'mb-5': minQuantity > 1 && smDevice}"
                    :loading="loadingAddMeal"
                    :inline="lgDevice")
                    span.text-nowrap {{$t('meal.add_to_cart')}}
                    .price-wrapper.ml-auto
                        .price.ml-3.ml-sm-5
                            s.font-size-sm(v-if="originalPrice !== (price ? price : details.price)") {{ originalPrice  | toCurrency(currency.locale, currency.currency_code) }}
                            span {{ price ? price : details.price  | toCurrency(currency.locale, currency.currency_code) }}

</template>
